import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { formatNumWithKMBAlt2, getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import { formatValue, HierarchicalTreeNode } from '../data/hierarchicalTreeData';
import { cloneDeep, isEmpty, kebabCase } from 'lodash';
import { nullAttrTypeOption } from 'components/pages/GrowthAnalysis';
import { FilterContextData } from 'contexts/FilterContext';

type GrowthMarginDriversContextValue = {
    selectedGrowthMarginDriver: { key: string; label: string };
    setSelectedGrowthMarginDriver: (driver: { key: string; label: string }) => void;
    selectedSubFilter: any;
    setSelectedSubFilter: (subFilter: any) => void;
    selectedBarForViewBreakdown: any;
    setSelectedBarForViewBreakdown: (bar: any) => void;
    // Hierarchical Tree related state
    fundFlowData: HierarchicalTreeNode | null;
    setFundFlowData: (data: HierarchicalTreeNode | null) => void;
    selectedFundFlowNode: HierarchicalTreeNode | null;
    setSelectedFundFlowNode: (node: HierarchicalTreeNode | null) => void;
    selectedNodes: any[];
    setSelectedNodes: (nodes: any[]) => void;
    decompositionFilterSelectedItems: any[];
    setDecompositionFilterSelectedItems: (items: any[]) => void;
    memoisedDecompositionFilterDropdownOptions: any[];
    decompositionGraphData: HierarchicalTreeNode | null;
    setDecompositionGraphData: (data: HierarchicalTreeNode | null) => void;
};

const GrowthMarginDriversContext = createContext<GrowthMarginDriversContextValue | null>(null);

export const GrowthMarginDriversContextProvider = ({ children }: { children: React.ReactNode }) => {
    const { appConfig } = useContext(UserContext);
    const { allDimAndAttributeTypeList, singleSelectedTier, idForRestOfComponent, timePeriodSelection } =
        useContext(FilterContextData);

    const profitPoolConfig = getConfig('profitPool', appConfig);
    const {
        growthMarginDriversDropDownOptionsMapping,
        defaultAttributeForData,
        excludedDimensions = [],
    } = profitPoolConfig;

    const growthMarginDriversDropDownOptions = useMemo(() => {
        return growthMarginDriversDropDownOptionsMapping[singleSelectedTier as any] || [];
    }, [singleSelectedTier, growthMarginDriversDropDownOptionsMapping]);

    const [selectedGrowthMarginDriver, setSelectedGrowthMarginDriver] = useState<{ key: string; label: string }>(
        growthMarginDriversDropDownOptionsMapping[singleSelectedTier as any]?.[0] || { key: '', label: '' },
    );

    const [selectedSubFilter, setSelectedSubFilter] = useState<any>(defaultAttributeForData);

    const [selectedBarForViewBreakdown, setSelectedBarForViewBreakdown] = useState<any>(null);

    // Hierarchical Tree related state
    const [fundFlowData, setFundFlowData] = useState<HierarchicalTreeNode | null>(null);
    const [selectedFundFlowNode, setSelectedFundFlowNode] = useState<HierarchicalTreeNode | null>(null);

    const [selectedNodes, setSelectedNodes] = useState<any[]>([]);
    const [decompositionGraphData, setDecompositionGraphData] = useState<HierarchicalTreeNode | null>(null);

    const [decompositionFilterDropdownOptions, setDecompositionFilterDropdownOptions] = useState({
        data: [] as any[],
        loading: false,
    });
    const [decompositionFilterSelectedItems, setDecompositionFilterSelectedItems] = useState<any[]>([
        { ...nullAttrTypeOption },
    ]);

    const memoisedDecompositionFilterDropdownOptions = useMemo(() => {
        const tempOptions: any[] = cloneDeep(decompositionFilterDropdownOptions.data);
        decompositionFilterSelectedItems.forEach((item) => {
            const attrTypeIndex = tempOptions.findIndex((elem: any) => elem.label === item?.label);
            if (attrTypeIndex > -1) tempOptions.splice(attrTypeIndex, 1);
        });
        return tempOptions;
    }, [decompositionFilterSelectedItems, decompositionFilterDropdownOptions]);

    useEffect(() => {
        if (growthMarginDriversDropDownOptions?.length > 0) {
            setSelectedGrowthMarginDriver(growthMarginDriversDropDownOptions[0]);
        }
    }, [growthMarginDriversDropDownOptions]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'], excludedDimensions);
            if (data.length) {
                setDecompositionFilterDropdownOptions({ data, loading: false });
            }
        }
    }, [allDimAndAttributeTypeList?.loading]);

    useEffect(() => {
        if (!isEmpty(selectedBarForViewBreakdown)) {
            const indexOfSelectedFilter = decompositionFilterDropdownOptions?.data?.findIndex(
                (item) => item.key === selectedSubFilter?.dataKey,
            );

            const currentAttributeLevel = decompositionFilterDropdownOptions.data[indexOfSelectedFilter];
            const nextAttributeLevel =
                decompositionFilterDropdownOptions.data[indexOfSelectedFilter + 1] ??
                decompositionFilterDropdownOptions.data[0];

            const selectedNode = {
                ...selectedBarForViewBreakdown,
                id: selectedBarForViewBreakdown?.attribute_ex_id,
                name: selectedBarForViewBreakdown?.attribute_ex_id,
                label: formatNumWithKMBAlt2(
                    selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][1] -
                        selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][0],
                    2,
                    2,
                ),
                variableValue: 1.0,
                status:
                    selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][1] -
                        selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][0] >
                    0
                        ? 'PNP'
                        : 'R',
                rate: 1.0,
                variableName: 'Selected',
                variableUp:
                    selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][1] -
                        selectedBarForViewBreakdown[selectedGrowthMarginDriver?.key][0] >
                    0,
                children: [],
                attributeExId: selectedBarForViewBreakdown?.attribute_ex_id,
                attributeType: selectedSubFilter?.dataKey,
                dimension: selectedSubFilter?.dimension,
                isAttribute: selectedSubFilter?.isAttribute,
                nextAttributeType: nextAttributeLevel?.key,
                nextDimension: nextAttributeLevel?.dimension,
                nextIsAttribute: nextAttributeLevel?.isAttribute,
            };

            setDecompositionGraphData(null);
            setSelectedNodes([selectedNode]);

            setDecompositionFilterSelectedItems([currentAttributeLevel, nextAttributeLevel, nullAttrTypeOption]);
        }
    }, [selectedBarForViewBreakdown]);

    useEffect(() => {
        setSelectedNodes([]);
        setDecompositionFilterSelectedItems([nullAttrTypeOption]);
        setDecompositionGraphData(null);
    }, [selectedSubFilter, selectedGrowthMarginDriver, singleSelectedTier, idForRestOfComponent, timePeriodSelection]);

    const value: GrowthMarginDriversContextValue = {
        selectedGrowthMarginDriver,
        setSelectedGrowthMarginDriver,
        selectedSubFilter,
        setSelectedSubFilter,
        selectedBarForViewBreakdown,
        setSelectedBarForViewBreakdown,
        fundFlowData,
        setFundFlowData,
        selectedFundFlowNode,
        setSelectedFundFlowNode,
        selectedNodes,
        setSelectedNodes,
        decompositionFilterSelectedItems,
        setDecompositionFilterSelectedItems,
        memoisedDecompositionFilterDropdownOptions,
        decompositionGraphData,
        setDecompositionGraphData,
    };

    return <GrowthMarginDriversContext.Provider value={value}>{children}</GrowthMarginDriversContext.Provider>;
};

export const useGrowthMarginDriversContext = () => {
    const context = useContext(GrowthMarginDriversContext);
    if (!context) {
        throw new Error('useGrowthMarginDriversContext must be used within a GrowthMarginDriversContextProvider');
    }
    return context;
};
